import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, API_BASE, getToken } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Gauge, Plus, Pencil, Trash2, Send, Paperclip, X, Search, CheckCircle2, AlertTriangle, Loader2, Download, MessageSquare, FileSpreadsheet, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { EmployeeSearchSelect } from "@/components/EmployeeSearchSelect";

const KPI_STATUSES = {
  draft: { label: "Draft", cls: "bg-gray-100 text-gray-700 border-gray-200" },
  pushed: { label: "Awaiting fix", cls: "bg-sky-100 text-sky-800 border-sky-200" },
  acknowledged: { label: "Fixed / Acknowledged", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  correction_requested: { label: "Disputed", cls: "bg-rose-100 text-rose-800 border-rose-200" },
};

const KPI_CATEGORIES = ["Receiving", "Shipping", "Company JC", "Procurements", "Repairs", "Admin", "Attendance", "Performance"];

function AddEditDialog({ open, initial, employees, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      setForm(initial
        ? { employee_id: initial.employee_id, date: initial.date, category: initial.category || "", errors: initial.errors || "" }
        : { employee_id: "", date: "", category: "", errors: "" });
    }
  }, [open, initial]);
  if (!form) return null;
  const save = async () => {
    if (!initial && !form.employee_id) { toast.error("Pick an employee."); return; }
    if (!form.date) { toast.error("Date of the error is required."); return; }
    if (!form.category) { toast.error("Pick a category."); return; }
    if (!form.errors.trim()) { toast.error("Describe the errors found / what needs fixing."); return; }
    setSaving(true);
    try {
      if (initial) {
        await api.patch(`/kpi/${initial.id}`, { date: form.date, category: form.category, errors: form.errors });
        toast.success("KPI updated");
      } else {
        await api.post("/kpi", form);
        toast.success("KPI created");
      }
      onSaved();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="kpi-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Gauge className="w-4 h-4" /> {initial ? "Edit KPI" : "New KPI"}</DialogTitle>
          <DialogDescription>{initial ? "Adjust the correction before or after pushing." : "Record errors found, then push to the employee's Tasks to fix or acknowledge."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {!initial && (
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Employee</Label>
              <div className="mt-1.5">
                <EmployeeSearchSelect
                  options={employees.map((e) => ({ id: e.id, label: `${e.last_name}, ${e.first_initial}`, sublabel: e.job_title || "" }))}
                  value={form.employee_id}
                  onChange={(v) => setForm((f) => ({ ...f, employee_id: v }))}
                  triggerPlaceholder="Search & pick an employee"
                  testId="kpi-employee-select"
                />
              </div>
            </div>
          )}
          <div>
            <Label className="text-xs uppercase tracking-widest text-gray-500">Date of error</Label>
            <Input className="mt-1.5" type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} data-testid="kpi-date-input" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-gray-500">Category</Label>
            <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
              <SelectTrigger className="mt-1.5" data-testid="kpi-category-select"><SelectValue placeholder="Pick a category" /></SelectTrigger>
              <SelectContent>
                {KPI_CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c} data-testid={`kpi-category-option-${c.replace(/\s+/g, "-").toLowerCase()}`}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-gray-500">Errors found / what needs fixing</Label>
            <Textarea className="mt-1.5" rows={5} value={form.errors} onChange={(e) => setForm((f) => ({ ...f, errors: e.target.value }))} placeholder="Describe the errors and what the employee needs to correct…" data-testid="kpi-errors-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={save} disabled={saving} data-testid="kpi-form-save">
            {saving ? "Saving…" : initial ? "Save changes" : "Create KPI"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function KpiComments({ record, onChanged }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const comments = record.comments || [];
  const post = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post(`/kpi/${record.id}/comments`, { text: text.trim() });
      setText("");
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to post comment"); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-3 pt-3 border-t border-summit-hairline">
      <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-gray-500">
        <MessageSquare className="w-3 h-3" /> Comments
      </div>
      {comments.length > 0 && (
        <div className="mt-2 space-y-1.5" data-testid={`kpi-comments-${record.id}`}>
          {comments.map((c) => (
            <div key={c.id} className="rounded-md px-3 py-2 border bg-gray-50 border-summit-hairline">
              <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">
                {c.by_name}{c.at ? ` · ${new Date(c.at).toLocaleString()}` : ""}
              </div>
              <p className="text-sm text-gray-700 mt-0.5 whitespace-pre-wrap">{c.text}</p>
            </div>
          ))}
        </div>
      )}
      <div className="mt-2 flex items-start gap-2">
        <Textarea rows={1} className="min-h-[38px] text-sm" value={text} onChange={(e) => setText(e.target.value)} placeholder="Write a comment…" data-testid={`kpi-comment-input-${record.id}`} />
        <Button size="sm" className="h-9 bg-summit-primary hover:bg-summit-primary-soft text-white shrink-0" onClick={post} disabled={busy || !text.trim()} data-testid={`kpi-comment-post-${record.id}`}>
          <Send className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}

export default function KPI() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [denied, setDenied] = useState(false);
  const [q, setQ] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [view, setView] = useState("all");
  const [exportFrom, setExportFrom] = useState("");
  const [exportTo, setExportTo] = useState("");
  const [exporting, setExporting] = useState(null);
  const fileRef = useRef(null);
  const uploadKpiRef = useRef(null);

  const runExport = async (fmt) => {
    setExporting(fmt);
    try {
      const params = new URLSearchParams({ fmt });
      if (exportFrom) params.set("date_from", exportFrom);
      if (exportTo) params.set("date_to", exportTo);
      const resp = await fetch(`${API_BASE}/kpi/export?${params}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!resp.ok) throw new Error();
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `kpi_report_${exportFrom || "all"}_${exportTo || "all"}.${fmt === "xlsx" ? "xlsx" : "pdf"}`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch { toast.error("Export failed"); }
    finally { setExporting(null); }
  };

  const load = async () => {
    try {
      const [r1, r2] = await Promise.all([api.get("/kpi"), api.get("/kpi/employees")]);
      setRows(r1.data);
      setEmployees(r2.data);
    } catch (e) {
      if (e?.response?.status === 403) setDenied(true);
      else toast.error(e?.response?.data?.detail || "Failed to load KPIs");
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows || [];
    return (rows || []).filter((r) => [r.employee_name, r.date, r.category, r.errors, r.created_by_name].filter(Boolean).join(" ").toLowerCase().includes(s));
  }, [rows, q]);

  const grouped = useMemo(() => {
    const map = new Map();
    filtered.forEach((r) => {
      const g = map.get(r.employee_id) || { employee_name: r.employee_name, items: [], byCategory: {}, disputed: 0, open: 0 };
      g.items.push(r);
      const cat = r.category || "Uncategorized";
      g.byCategory[cat] = (g.byCategory[cat] || 0) + 1;
      if (r.status === "correction_requested") g.disputed += 1;
      if (r.status === "pushed" || r.status === "draft") g.open += 1;
      map.set(r.employee_id, g);
    });
    return [...map.entries()]
      .map(([eid, g]) => ({ employee_id: eid, ...g }))
      .sort((a, b) => b.items.length - a.items.length || a.employee_name.localeCompare(b.employee_name));
  }, [filtered]);

  const pushKpi = async (r) => {
    setBusyId(r.id);
    try {
      await api.post(`/kpi/${r.id}/push`);
      toast.success(`Pushed to ${r.employee_name}'s tasks`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to push"); }
    finally { setBusyId(null); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete the ${r.date} KPI for ${r.employee_name}?`)) return;
    try {
      await api.delete(`/kpi/${r.id}`);
      toast.success("KPI deleted");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to delete"); }
  };

  const startUpload = (r) => { uploadKpiRef.current = r.id; fileRef.current?.click(); };
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    const kid = uploadKpiRef.current;
    if (!file || !kid) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusyId(kid);
    try {
      await api.post(`/kpi/${kid}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Attachment added");
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setBusyId(null); }
  };

  const downloadAttachment = async (r, a) => {
    try {
      const resp = await fetch(`${API_BASE}/kpi/${r.id}/attachments/${a.id}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!resp.ok) throw new Error();
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = a.filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const deleteAttachment = async (r, a) => {
    try {
      await api.delete(`/kpi/${r.id}/attachments/${a.id}`);
      toast.success("Attachment removed");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to remove"); }
  };

  if (denied) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center" data-testid="kpi-no-access">
        <Gauge className="w-10 h-10 mx-auto text-gray-300" />
        <p className="text-sm text-gray-500 mt-4">You don't have access to KPI tracking. Ask an admin to grant it in Admin Settings.</p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <input ref={fileRef} type="file" className="hidden" onChange={onFile} data-testid="kpi-file-input" />
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 flex items-center gap-1.5">
            <Gauge className="w-3 h-3" /> Team
          </div>
          <h1 className="font-display text-3xl font-medium tracking-tight mt-1" data-testid="kpi-heading">KPI Tracking</h1>
          <p className="text-sm text-gray-500 mt-1">Record errors for employees to fix, attach backup docs, then push to their Tasks to fix/acknowledge or dispute.</p>
        </div>
        <div className="flex items-center gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" data-testid="kpi-export-btn">
                <Download className="w-4 h-4 mr-1.5" /> Export
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-3 space-y-3" align="end" data-testid="kpi-export-pop">
              <div className="font-mono text-[9px] uppercase tracking-widest text-gray-500">Report range (date of error)</div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-gray-500">From</Label>
                  <Input className="h-8 mt-1 text-xs" type="date" value={exportFrom} onChange={(e) => setExportFrom(e.target.value)} data-testid="kpi-export-from" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-gray-500">To</Label>
                  <Input className="h-8 mt-1 text-xs" type="date" value={exportTo} onChange={(e) => setExportTo(e.target.value)} data-testid="kpi-export-to" />
                </div>
              </div>
              <p className="text-[10px] text-gray-400">Leave blank to include everything. Report is grouped by employee.</p>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="flex-1 h-8 text-xs" onClick={() => runExport("xlsx")} disabled={!!exporting} data-testid="kpi-export-xlsx">
                  {exporting === "xlsx" ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5 mr-1" />} Excel
                </Button>
                <Button size="sm" variant="outline" className="flex-1 h-8 text-xs" onClick={() => runExport("pdf")} disabled={!!exporting} data-testid="kpi-export-pdf">
                  {exporting === "pdf" ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <FileText className="w-3.5 h-3.5 mr-1" />} PDF
                </Button>
              </div>
            </PopoverContent>
          </Popover>
          <Button className="bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="kpi-new-btn">
            <Plus className="w-4 h-4 mr-1.5" /> New KPI
          </Button>
        </div>
      </div>

      <div className="mt-6 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input className="pl-9" placeholder="Search by employee, date, category, error…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="kpi-search" />
        </div>
        <div className="flex rounded-md border border-summit-hairline overflow-hidden">
          <button
            type="button"
            onClick={() => setView("all")}
            className={`px-3 py-2 font-mono text-[10px] uppercase tracking-widest transition-colors ${view === "all" ? "bg-summit-primary text-white" : "bg-white text-gray-600 hover:bg-summit-glacier"}`}
            data-testid="kpi-view-all"
          >All KPIs</button>
          <button
            type="button"
            onClick={() => setView("by_employee")}
            className={`px-3 py-2 font-mono text-[10px] uppercase tracking-widest transition-colors border-l border-summit-hairline ${view === "by_employee" ? "bg-summit-primary text-white" : "bg-white text-gray-600 hover:bg-summit-glacier"}`}
            data-testid="kpi-view-by-employee"
          >By employee</button>
        </div>
      </div>

      {view === "by_employee" && (
        <div className="mt-6 space-y-3" data-testid="kpi-grouped-list">
          {rows !== null && grouped.length === 0 && (
            <div className="border border-dashed border-summit-hairline rounded-lg p-10 text-center" data-testid="kpi-grouped-empty">
              <Gauge className="w-8 h-8 mx-auto text-gray-300" />
              <p className="text-sm text-gray-400 mt-3">{q ? "No KPIs match your search." : "No KPIs yet."}</p>
            </div>
          )}
          {grouped.map((g) => (
            <div key={g.employee_id} className="border border-summit-hairline rounded-lg bg-white p-5" data-testid={`kpi-group-${g.employee_id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-display text-base font-medium tracking-tight">{g.employee_name}</span>
                <span className="inline-flex px-2 py-0.5 rounded-full bg-gray-900 text-white font-mono text-[10px] uppercase tracking-widest" data-testid={`kpi-group-count-${g.employee_id}`}>
                  {g.items.length} correction{g.items.length === 1 ? "" : "s"}
                </span>
                {g.open > 0 && (
                  <span className="inline-flex px-1.5 py-0.5 rounded-full border border-sky-200 bg-sky-50 text-sky-700 font-mono text-[10px] uppercase tracking-widest">{g.open} open</span>
                )}
                {g.disputed > 0 && (
                  <span className="inline-flex px-1.5 py-0.5 rounded-full border border-rose-200 bg-rose-50 text-rose-700 font-mono text-[10px] uppercase tracking-widest">{g.disputed} disputed</span>
                )}
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`kpi-group-categories-${g.employee_id}`}>
                {Object.entries(g.byCategory).sort((a, b) => b[1] - a[1]).map(([cat, n]) => (
                  <span key={cat} className="inline-flex px-1.5 py-0.5 rounded border border-indigo-200 bg-indigo-50 text-indigo-700 font-mono text-[10px] uppercase tracking-widest">
                    {cat} · {n}
                  </span>
                ))}
              </div>
              <div className="mt-3 divide-y divide-summit-hairline">
                {g.items.map((r) => {
                  const st = KPI_STATUSES[r.status] || KPI_STATUSES.draft;
                  return (
                    <div key={r.id} className="py-2 flex items-center gap-2 flex-wrap" data-testid={`kpi-group-item-${r.id}`}>
                      <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500 w-[80px] shrink-0">{r.date}</span>
                      {r.category && <span className="inline-flex px-1.5 py-0.5 rounded border border-indigo-200 bg-indigo-50 text-indigo-700 font-mono text-[9px] uppercase tracking-widest shrink-0">{r.category}</span>}
                      <span className={`inline-flex px-1.5 py-0.5 rounded-full border font-mono text-[9px] uppercase tracking-widest shrink-0 ${st.cls}`}>{st.label}</span>
                      <span className="text-xs text-gray-600 truncate flex-1 min-w-[160px]">{r.errors}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {view === "all" && (
      <div className="mt-6 space-y-3">
        {rows === null && <p className="text-sm text-gray-400">Loading…</p>}
        {rows !== null && filtered.length === 0 && (
          <div className="border border-dashed border-summit-hairline rounded-lg p-10 text-center" data-testid="kpi-empty">
            <Gauge className="w-8 h-8 mx-auto text-gray-300" />
            <p className="text-sm text-gray-400 mt-3">{q ? "No KPIs match your search." : "No KPIs yet — record the first correction."}</p>
          </div>
        )}
        {filtered.map((r) => {
          const st = KPI_STATUSES[r.status] || KPI_STATUSES.draft;
          const canManage = r.created_by === user.id || user.role === "admin";
          const lastCorrection = (r.corrections || [])[r.corrections?.length - 1];
          return (
            <div key={r.id} className="border border-summit-hairline rounded-lg bg-white p-5" data-testid={`kpi-card-${r.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display text-base font-medium tracking-tight" data-testid={`kpi-employee-${r.id}`}>{r.employee_name}</span>
                    <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500">{r.date}</span>
                    {r.category && (
                      <span className="inline-flex px-1.5 py-0.5 rounded border border-indigo-200 bg-indigo-50 text-indigo-700 font-mono text-[10px] uppercase tracking-widest" data-testid={`kpi-category-${r.id}`}>{r.category}</span>
                    )}
                    <span className={`inline-flex px-1.5 py-0.5 rounded-full border font-mono text-[10px] uppercase tracking-widest ${st.cls}`} data-testid={`kpi-status-${r.id}`}>{st.label}</span>
                  </div>
                  <div className="mt-2">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Errors found / to fix</div>
                    <p className="text-sm text-gray-700 mt-0.5 whitespace-pre-wrap" data-testid={`kpi-errors-${r.id}`}>{r.errors}</p>
                  </div>
                  {r.status === "correction_requested" && lastCorrection && (
                    <div className="mt-2 border border-rose-100 bg-rose-50/60 rounded-md px-3 py-2" data-testid={`kpi-correction-${r.id}`}>
                      <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-rose-700">
                        <AlertTriangle className="w-3 h-3" /> Disputed by {lastCorrection.by_name}
                      </div>
                      <p className="text-sm text-rose-800 mt-1 whitespace-pre-wrap">{lastCorrection.comment}</p>
                    </div>
                  )}
                  {r.status !== "correction_requested" && (r.corrections || []).length > 0 && (
                    <div className="mt-2 space-y-1" data-testid={`kpi-past-disputes-${r.id}`}>
                      {r.corrections.map((c, i) => (
                        <div key={i} className="font-mono text-[10px] text-rose-600">Disputed earlier — {c.by_name}: <span className="text-gray-600 normal-case">{c.comment}</span></div>
                      ))}
                    </div>
                  )}
                  {r.status === "acknowledged" && (
                    <div className="mt-2 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-emerald-700" data-testid={`kpi-ack-${r.id}`}>
                      <CheckCircle2 className="w-3 h-3" /> Fixed / acknowledged {r.acknowledged_at ? new Date(r.acknowledged_at).toLocaleDateString() : ""}
                    </div>
                  )}
                  {r.ack_comment && (
                    <div className="mt-1.5 border border-emerald-100 bg-emerald-50/60 rounded-md px-3 py-2" data-testid={`kpi-ack-comment-${r.id}`}>
                      <div className="font-mono text-[10px] uppercase tracking-widest text-emerald-700">Employee comment — {r.ack_by_name}</div>
                      <p className="text-sm text-emerald-900 mt-1 whitespace-pre-wrap">{r.ack_comment}</p>
                    </div>
                  )}
                  {(r.attachments || []).length > 0 && (
                    <div className="mt-2 space-y-1" data-testid={`kpi-attachments-${r.id}`}>
                      {r.attachments.map((a) => (
                        <div key={a.id} className="flex items-center gap-2 text-xs text-gray-600">
                          <Paperclip className="w-3 h-3 shrink-0 text-gray-400" />
                          <button type="button" className="truncate font-medium hover:underline" onClick={() => downloadAttachment(r, a)} data-testid={`kpi-attach-dl-${a.id}`}>{a.filename}</button>
                          <span className="text-gray-400">{Math.round(a.size / 1024)} KB</span>
                          {canManage && (
                            <button type="button" className="p-0.5 text-gray-400 hover:text-rose-600" onClick={() => deleteAttachment(r, a)} data-testid={`kpi-attach-del-${a.id}`}>
                              <X className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="mt-2 font-mono text-[9px] uppercase text-gray-400">By {r.created_by_name} · {new Date(r.created_at).toLocaleDateString()}</div>
                  <KpiComments record={r} onChanged={load} />
                </div>
                <div className="flex items-center gap-1.5 shrink-0 flex-wrap">
                  {(r.status === "draft" || r.status === "correction_requested") && (
                    <Button size="sm" className="h-8 bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={() => pushKpi(r)} disabled={busyId === r.id} data-testid={`kpi-push-${r.id}`}>
                      {busyId === r.id ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
                      {r.status === "correction_requested" ? "Re-push" : "Push to employee"}
                    </Button>
                  )}
                  {r.status !== "acknowledged" && (
                    <Button size="sm" variant="outline" className="h-8" onClick={() => startUpload(r)} disabled={busyId === r.id} data-testid={`kpi-attach-btn-${r.id}`}>
                      <Paperclip className="w-3.5 h-3.5 mr-1.5" /> Attach
                    </Button>
                  )}
                  {canManage && r.status !== "acknowledged" && (
                    <button type="button" className="p-1.5 rounded text-gray-400 hover:text-summit-primary" onClick={() => { setEditing(r); setDialogOpen(true); }} data-testid={`kpi-edit-${r.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {canManage && (
                    <button type="button" className="p-1.5 rounded text-gray-400 hover:text-rose-600" onClick={() => remove(r)} data-testid={`kpi-delete-${r.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      )}

      <AddEditDialog
        open={dialogOpen}
        initial={editing}
        employees={employees}
        onClose={() => { setDialogOpen(false); setEditing(null); }}
        onSaved={load}
      />
    </div>
  );
}
